import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { downloadLanguageFile } from "../redux/slices/languagesSlice";
import enFallback from "../languages/en.json";
import arLocal from "../languages/ar.json";

// Local language files bundled with the app — covers admin-specific keys
// that may not yet exist in the CDN language files.
const localLanguageFiles = {
  ar: arLocal,
};

const LanguageContext = createContext();

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useTranslation must be used within LanguageProvider");
  return context;
};

export const LanguageProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { languages, cachedContent } = useSelector((state) => state.languages);

  const [activeLanguageKey, setActiveLanguageKey] = useState(
    () => localStorage.getItem("selectedLanguage") || "en"
  );
  // Start with English translations as default
  const [translations, setTranslations] = useState(enFallback);
  const [activeLanguage, setActiveLanguage] = useState(null);

  const isRTL = ["ar", "he", "fa", "ur"].includes(activeLanguageKey);

  // Mirror the entire layout when the active language is right-to-left so the
  // sidebar / paddings / etc. flip via Tailwind's logical utilities (ms-*, ps-*,
  // start-*). The `lang` attribute also drives the DIN Next LT Arabic webfont.
  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = activeLanguageKey || "en";
  }, [activeLanguageKey, isRTL]);

  // t(key) — returns translation or falls back to English value, then the key itself
  const t = useCallback(
    (key) => translations[key] ?? enFallback[key] ?? key,
    [translations]
  );

  // tStatus(value) — translates a backend status enum value (e.g. "paid", "in_transit").
  // Falls back to a Capitalised, space-separated form of the raw value if no key exists.
  const tStatus = useCallback(
    (value) => {
      if (value === null || value === undefined || value === "") return t("status.na");
      const key = `status.${String(value).toLowerCase()}`;
      const translated = translations[key] ?? enFallback[key];
      if (translated) return translated;
      return String(value)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    [translations, t]
  );

  // Load a language file and merge missing keys from en.json
  const applyLanguageContent = useCallback((key, content) => {
    if (!content || typeof content !== "object") return;
    // Merge order: enFallback (English base) → local bundled file (admin keys) → CDN content (overrides)
    const local = localLanguageFiles[key] || {};
    const merged = { ...enFallback, ...local, ...content };
    setTranslations(merged);
  }, []);

  // Called when user picks a language from the dropdown
  const setLanguage = useCallback(
    async (language) => {
      setActiveLanguageKey(language.key);
      setActiveLanguage(language);
      localStorage.setItem("selectedLanguage", language.key);

      if (language.key === "en") {
        setTranslations(enFallback);
        return;
      }

      // Use cached content if available
      if (cachedContent[language.key] && Object.keys(cachedContent[language.key]).length > 0) {
        applyLanguageContent(language.key, cachedContent[language.key]);
        return;
      }

      // Otherwise fetch from CDN URL
      if (language.url) {
        try {
          const result = await dispatch(downloadLanguageFile(language)).unwrap();
          applyLanguageContent(language.key, result.content);
        } catch (err) {
          console.error("Failed to load language file, falling back to English:", err);
          // Still apply local file if available
          const local = localLanguageFiles[language.key] || {};
          setTranslations({ ...enFallback, ...local });
        }
      } else if (localLanguageFiles[language.key]) {
        // No CDN URL but we have a local file — use it
        applyLanguageContent(language.key, localLanguageFiles[language.key]);
      }
    },
    [dispatch, cachedContent, applyLanguageContent]
  );

  // On initial load — if a non-English language was saved, restore it once languages are fetched
  useEffect(() => {
    if (activeLanguageKey === "en" || languages.length === 0) return;

    const savedLang = languages.find((l) => l.key === activeLanguageKey);
    if (!savedLang) return;

    setActiveLanguage(savedLang);

    // If already cached, apply immediately
    if (cachedContent[activeLanguageKey] && Object.keys(cachedContent[activeLanguageKey]).length > 0) {
      applyLanguageContent(activeLanguageKey, cachedContent[activeLanguageKey]);
    } else if (savedLang.url) {
      dispatch(downloadLanguageFile(savedLang))
        .unwrap()
        .then((result) => applyLanguageContent(savedLang.key, result.content))
        .catch(() => setTranslations(enFallback));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages]);

  return (
    <LanguageContext.Provider
      value={{ t, tStatus, setLanguage, activeLanguageKey, activeLanguage, isRTL, translations }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
